import React from "react";
import { useHistory } from "react-router-dom";
import cx from "classnames";
import styles from "./DetailClinic.module.css";

import Footer from "../../../layouts/Home/Footer";
import Button from "../../../components/Button";
import Spinner from "../../../components/Spinner";
import { findLabelRoleUser } from "../../../../utils/constants";
import {
  handleWhatsAppMessage,
  transformOperatingHours,
  transformDoctorSchedules,
  calculateYearsInOperation,
  generateClinicContactMessage,
} from "../../../../utils/utils";

const Container = ({ children, style, className }) => {
  return (
    <div style={style} className={cx(className)}>
      <div className={styles.container}>{children}</div>
    </div>
  );
};

export default function DetailClinicComponents({
  dataProfile,
  businessAddress,
  operatingHours,
  doctorSchedules,
  isLoadingBusinessProfile,
  isLoadingAddress,
  isLoadingHours,
  isLoadingDoctors,
  dataDoctors = [],
}) {
  const history = useHistory();

  // Transform data using utility functions
  const transformedOperatingHours = React.useMemo(() => {
    return transformOperatingHours(operatingHours);
  }, [operatingHours]);

  const transformedDoctors = React.useMemo(() => {
    return transformDoctorSchedules(
      doctorSchedules,
      dataDoctors,
      findLabelRoleUser
    );
  }, [doctorSchedules, dataDoctors]);

  // Calculate years in operation
  const yearsInOperation = calculateYearsInOperation(
    dataProfile.yearEstablished
  );

  // Services data
  const services = [
    {
      icon: "fas fa-stethoscope",
      title: "Konsultasi Umum",
      description: "Pemeriksaan kesehatan rutin",
    },
    {
      icon: "fas fa-heart",
      title: "Facial Wajah",
      description: "Perawatan wajah untuk kesehatan kulit",
    },
    {
      icon: "fas fa-capsules",
      title: "Obat",
      description: "Penyediaan obat-obatan yang diperlukan",
    },
  ];

  const isLoading =
    isLoadingBusinessProfile ||
    isLoadingAddress ||
    isLoadingHours ||
    isLoadingDoctors;

  // WhatsApp contact handler
  const handleContactClinic = () => {
    if (dataProfile.phoneNumber) {
      const message = generateClinicContactMessage(dataProfile.businessName);
      handleWhatsAppMessage(dataProfile.phoneNumber, message);
    }
  };

  return (
    <div className={styles.mainWrapper}>
      <Spinner loading={isLoading}>
        {/* Header Section */}
        <Container className={styles.headerSection}>
          <div className="d-flex flex-column">
            <div
              className={styles.backButton}
              onClick={() => history.push("/pasien")}
            >
              <i className="fas fa-arrow-left"></i>
              <p className={styles.backButtonText}>Kembali</p>
            </div>

            <div className={styles.clinicHeader}>
              <div className={styles.clinicImageWrapper}>
                <img
                  src={
                    dataProfile.businessLogo ||
                    "https://picsum.photos/seed/picsum/200/300"
                  }
                  alt="Clinic"
                  className={styles.clinicImage}
                  onError={(e) => {
                    e.target.src = "https://picsum.photos/seed/picsum/200/300";
                  }}
                />
              </div>

              <div className={styles.clinicInfo}>
                <h1 className={styles.clinicTitle}>
                  {dataProfile.businessName || "Nama Klinik"}
                </h1>

                <div className={styles.contactInfo}>
                  <i className="fas fa-map-marker-alt text-primary"></i>
                  <p
                    className={styles.contactText}
                    style={{ maxWidth: "600px" }}
                  >
                    {businessAddress?.address || "Alamat tidak tersedia"}
                  </p>
                </div>

                <div className={styles.contactInfo}>
                  <i className="fas fa-phone-alt text-success"></i>
                  <p className={styles.contactText}>
                    {dataProfile.phoneNumber || "Nomor telepon tidak tersedia"}
                  </p>
                </div>

                <div className={styles.buttonGroup}>
                  <Button
                    leftIcon={<i className="far fa-calendar-alt"></i>}
                    label="Buat Janji Temu"
                    className="rounded-lg"
                    onClick={() =>
                      history.push(
                        `/reservation-pasien?serviceId=3&clinicId=${dataProfile.id}`
                      )
                    }
                  />
                  <Button
                    label="Hubungi Klinik"
                    variant="outline-primary"
                    className="rounded-lg"
                    onClick={handleContactClinic}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>

        <Container className={styles.contentSection}>
          <div className="d-flex flex-column">
            {/* About & Hours Section */}
            <div className="row mb-4">
              <div className="col-lg-8 col-md-12 mb-3 mb-lg-0">
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div
                      className={cx(styles.iconWrapper, styles.iconWrapperBlue)}
                    >
                      <i
                        className="fas fa-clinic-medical"
                        style={{ fontSize: 18, color: "#1D4ED8" }}
                      ></i>
                    </div>
                    <h2 className={styles.cardTitle}>Tentang Klinik</h2>
                  </div>

                  <p className={styles.aboutText}>
                    {dataProfile.about ||
                      "Klinik terpercaya dengan layanan kesehatan komprehensif dan dokter berpengalaman. Kami berkomitmen memberikan pelayanan terbaik untuk kesehatan Anda dengan fasilitas modern dan teknologi terdepan."}
                  </p>

                  <hr className={styles.cardHorizontalLine} />

                  <div className={styles.statsRow}>
                    <div className={styles.statItem}>
                      <p className={cx(styles.statNumber, "text-primary")}>
                        {yearsInOperation > 0 ? `${yearsInOperation}+` : "New"}
                      </p>
                      <p className={styles.statLabel}>
                        {yearsInOperation > 0
                          ? "Tahun Pengalaman"
                          : "Klinik Baru"}
                      </p>
                    </div>
                    <div className={styles.statItem}>
                      <p className={cx(styles.statNumber, "text-success")}>
                        {transformedDoctors.length}
                      </p>
                      <p className={styles.statLabel}>Dokter Tersedia</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 col-md-12">
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div
                      className={cx(
                        styles.iconWrapper,
                        styles.iconWrapperPurple
                      )}
                    >
                      <i
                        className="far fa-clock"
                        style={{ color: "#886CC0", fontSize: 18 }}
                      ></i>
                    </div>
                    <h2 className={styles.cardTitle}>Jam Operasional</h2>
                  </div>

                  <div className="d-flex flex-column">
                    {transformedOperatingHours.length > 0 ? (
                      transformedOperatingHours.map((hour, index) => (
                        <div key={index} className={styles.hoursItem}>
                          <p className={styles.hoursDay}>{hour.day}</p>
                          <p
                            className={cx(
                              styles.hoursTime,
                              hour.isClose
                                ? styles.hoursTimeClosed
                                : styles.hoursTimeOpen
                            )}
                          >
                            {hour.isClose ? "Tutup" : hour.time}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className={styles.noDataMessage}>
                        <p>Jam operasional belum diatur</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Services Section */}
            <div className={cx(styles.card, "mb-4")}>
              <div className={styles.cardHeader}>
                <div
                  className={cx(styles.iconWrapper, styles.iconWrapperGreen)}
                >
                  <i
                    className="fas fa-hand-holding-medical text-success"
                    style={{ fontSize: 18 }}
                  ></i>
                </div>
                <h2 className={styles.cardTitle}>Layanan Kami</h2>
              </div>

              <div className={styles.servicesGrid}>
                {services.map((service, index) => (
                  <div key={index} className={styles.serviceCard}>
                    <div className={styles.serviceIcon}>
                      <i
                        className={service.icon}
                        style={{ fontSize: 20, color: "#FFF" }}
                      ></i>
                    </div>
                    <h3 className={styles.serviceTitle}>{service.title}</h3>
                    <p className={styles.serviceDescription}>
                      {service.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Doctors Section */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={cx(styles.iconWrapper, styles.iconWrapperPink)}>
                  <i
                    className="fas fa-stethoscope"
                    style={{ color: "#D653C1", fontSize: 18 }}
                  ></i>
                </div>
                <h2 className={styles.cardTitle}>
                  Dokter Kami ({transformedDoctors.length})
                </h2>
              </div>

              <div className={styles.doctorsGrid}>
                {transformedDoctors.length > 0 ? (
                  transformedDoctors.map((doctor, index) => (
                    <div key={doctor.doctorId} className={styles.doctorCard}>
                      <div className={styles.doctorBody}>
                        <div className={styles.doctorHeader}>
                          <img
                            src={doctor.imageUrl}
                            alt={doctor.name}
                            className={styles.doctorImage}
                          />
                          <div className={styles.doctorInfo}>
                            <h3 className={styles.doctorName}>{doctor.name}</h3>
                            <p className={styles.doctorSpecialty}>
                              {doctor.specialty}
                            </p>
                            <p className={styles.doctorExperience}>
                              {doctor.experience}
                            </p>
                          </div>
                        </div>

                        <div className="d-flex flex-column w-100">
                          <div className={styles.scheduleHeader}>
                            <i
                              className="far fa-calendar-alt"
                              style={{ fontSize: 16 }}
                            ></i>
                            <p className={styles.scheduleTitle}>
                              Jadwal Praktik
                            </p>
                          </div>

                          {doctor.schedule.map((schedule, indexS) => (
                            <div key={indexS} className={styles.scheduleItem}>
                              <div className={styles.scheduleDay}>
                                <p className={styles.scheduleDayText}>
                                  {schedule.day}
                                </p>
                              </div>
                              <p
                                className={cx(
                                  styles.scheduleTime,
                                  schedule.isClose
                                    ? styles.scheduleTimeClosed
                                    : styles.scheduleTimeActive
                                )}
                              >
                                {schedule.isClose ? "Libur" : schedule.time}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-3 w-100">
                        <Button
                          label={`Buat janji dengan dokter`}
                          fullWidth
                          className="rounded"
                          onClick={() =>
                            history.push(
                              `/reservation-pasien?serviceId=3&clinicId=${dataProfile.id}&doctorId=${doctor.doctorId}`
                            )
                          }
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.noDataMessage}>
                    <p>Belum ada dokter yang tersedia</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </Spinner>

      <Footer />
    </div>
  );
}
